import * as THREE from 'three';
import type { MapDefinition } from '../../../shared/game/world-types.js';
import { conditions } from './conditions.js';
import { boltSegments, flashAt, strikesBetween, thunderBetween } from './lightning.js';
import type { CritterGround } from '../critters/critter-ground.js';

/** What the lighting rig exposes so the weather can dim and thicken it each frame, after the time of day has set it. */
export interface WeatherRig {
  lights: { hemisphere: THREE.HemisphereLight; sun: THREE.DirectionalLight; fill: THREE.DirectionalLight; rim: THREE.DirectionalLight };
  fog: THREE.Fog;
}

const RAIN_STREAKS = 2600, RAIN_HEIGHT = 26;
const mistFragment = `varying vec2 vWorld; varying float vWeight; uniform float uTime, uMist, uClear; uniform vec3 uColor; uniform vec2 uFocus;
  float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
  float noise(vec2 p){ vec2 i = floor(p), f = fract(p); f = f * f * (3. - 2. * f);
    return mix(mix(hash(i), hash(i + vec2(1., 0.)), f.x), mix(hash(i + vec2(0., 1.)), hash(i + vec2(1., 1.)), f.x), f.y); }
  float fbm(vec2 p){ float v = 0., a = .5; for (int i = 0; i < 4; i++) { v += a * noise(p); p = p * 2.03 + vec2(17.3, 9.1); a *= .5; } return v; }
  void main(){
    // Banks drift slowly inland on two layers; thickest over the sea margin and the low ground, thinning around the party so heroes stay readable.
    vec2 drift = vec2(uTime * .9, uTime * .35);
    float banks = fbm(vWorld * .03 + drift * .02) * .6 + fbm(vWorld * .08 - drift.yx * .03) * .4;
    float near = smoothstep(4. * uClear, 20. * uClear, distance(vWorld, uFocus));
    float alpha = (.3 + .7 * smoothstep(.3, .68, banks)) * vWeight * uMist * near * .85;
    gl_FragColor = vec4(uColor, alpha);
  }`;
const rainVertex = `attribute vec3 seed; attribute float tip; uniform float uTime, uRain, uSize; uniform vec3 uFocus; uniform vec2 uWind; varying float vAlpha;
  void main(){
    float speed = 21. + seed.z * 9.;
    float y = mod(seed.y * ${RAIN_HEIGHT.toFixed(1)} - uTime * speed, ${RAIN_HEIGHT.toFixed(1)});
    // Wrapped in the world, so the field stays put as the camera pans, and thinned by how hard it is raining.
    vec2 cell = fract(vec2(seed.x, fract(seed.z * 7.31 + seed.y * 3.7)) - uFocus.xz / (2. * uSize));
    vec2 xz = uFocus.xz + (cell - .5) * 2. * uSize;
    float height = y + tip * (.9 + seed.z * .6);
    vec3 p = vec3(xz.x + uWind.x * height, height + uFocus.y, xz.y + uWind.y * height);
    float kept = step(fract(seed.z * 13.7 + seed.x * 5.3), uRain);
    vAlpha = kept * uRain * (.34 - tip * .24);
    gl_Position = kept > .5 ? projectionMatrix * viewMatrix * vec4(p, 1.) : vec4(2., 2., 2., 1.);
  }`;

/**
 * Sea mist and rain over the map, and their effect on the light: a dimmer, greyer, closer sky and darker, wet ground. The values
 * come from `conditions` (a pure function of the clock, or a forced preset), so every player sees the same weather.
 *
 * Mist is a drifting translucent layer 1.6 m up, weighted toward the coast and marsh; rain is a field of falling streaks that
 * follows the camera, worked out entirely on the GPU from time. In heavy rain there are thunderstorms (`lightning.ts`): a flash
 * that lights the whole scene, a jagged bolt landing somewhere nearby, and thunder that follows after a delay through `onThunder`.
 */
export class WeatherLayer {
  private readonly mist: THREE.Mesh;
  private readonly mistMaterial: THREE.ShaderMaterial;
  private readonly rain: THREE.LineSegments;
  private readonly rainMaterial: THREE.ShaderMaterial;
  private readonly dry = new THREE.Color();
  private readonly colour = new THREE.Color();
  private readonly white = new THREE.Color(1, 1, 1);
  private readonly flashColour = new THREE.Color(0xdbe5ff);
  private readonly grey = new THREE.Color();
  private readonly bolt: THREE.LineSegments;
  private readonly boltMaterial: THREE.LineBasicMaterial;
  private boltSeed = -1;
  private lastSeconds: number | null = null;
  /** Called when a strike's thunder reaches the camera, with its loudness (0–1) and distance (0 overhead, 1 on the horizon). */
  onThunder: ((strength: number, distance: number) => void) | null = null;

  constructor(scene: THREE.Scene, private renderer: THREE.WebGLRenderer, map: MapDefinition, private ground: CritterGround, private floor: THREE.Mesh | null, private seed = 0) {
    // Mist: a coarse grid whose vertices carry how much mist belongs there.
    const geometry = new THREE.PlaneGeometry(map.width + 200, map.depth + 200, 80, 60).rotateX(-Math.PI / 2);
    const position = geometry.getAttribute('position'), weights = new Float32Array(position.count), marshes = (map.scenery?.districts ?? []).filter(d => d.biome === 'marsh');
    for (let i = 0; i < weights.length; i++) {
      const x = position.getX(i), z = position.getZ(i);
      position.setY(i, ground.height(x, z));
      const coast = 1 - THREE.MathUtils.smoothstep(ground.shoreDistance(x, z), 6, 150);
      const marsh = Math.max(0, ...marshes.map(d => 1 - THREE.MathUtils.smoothstep(Math.hypot(x - d.x, z - d.z), d.radius * .5, d.radius * 1.4)));
      weights[i] = Math.max(.3, coast, marsh);
    }
    geometry.setAttribute('weight', new THREE.BufferAttribute(weights, 1));
    this.mistMaterial = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, uniforms: { uTime: { value: 0 }, uMist: { value: 0 }, uClear: { value: 1 }, uColor: { value: new THREE.Color(0xc9d1d6) }, uFocus: { value: new THREE.Vector2() } },
      vertexShader: 'attribute float weight; varying vec2 vWorld; varying float vWeight; void main(){ vWorld = position.xz; vWeight = weight; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.); }',
      fragmentShader: mistFragment,
    });
    this.mist = new THREE.Mesh(geometry, this.mistMaterial); this.mist.position.y = 1.6; this.mist.renderOrder = 3; this.mist.frustumCulled = false; this.mist.visible = false;
    scene.add(this.mist);

    // Rain: each streak is two vertices (tail and head) whose place is worked out in the vertex shader.
    const seeds = new Float32Array(RAIN_STREAKS * 6), tips = new Float32Array(RAIN_STREAKS * 2);
    for (let i = 0; i < RAIN_STREAKS; i++) {
      const a = Math.random(), b = Math.random(), c = Math.random();
      for (let k = 0; k < 2; k++) { seeds.set([a, b, c], (i * 2 + k) * 3); tips[i * 2 + k] = k; }
    }
    const rainGeometry = new THREE.BufferGeometry();
    rainGeometry.setAttribute('position', new THREE.BufferAttribute(new Float32Array(RAIN_STREAKS * 6), 3));
    rainGeometry.setAttribute('seed', new THREE.BufferAttribute(seeds, 3)); rainGeometry.setAttribute('tip', new THREE.BufferAttribute(tips, 1));
    this.rainMaterial = new THREE.ShaderMaterial({
      transparent: true, depthWrite: false, vertexShader: rainVertex, fragmentShader: 'varying float vAlpha; uniform vec3 uColor; void main(){ gl_FragColor = vec4(uColor, vAlpha); }',
      uniforms: { uTime: { value: 0 }, uRain: { value: 0 }, uFocus: { value: new THREE.Vector3() }, uSize: { value: 40 }, uWind: { value: new THREE.Vector2(.12, .06) }, uColor: { value: new THREE.Color(0x9fb0c0) } },
    });
    this.rain = new THREE.LineSegments(rainGeometry, this.rainMaterial); this.rain.frustumCulled = false; this.rain.visible = false; this.rain.renderOrder = 4;
    scene.add(this.rain);
    this.boltMaterial = new THREE.LineBasicMaterial({ color: 0xe6edff, transparent: true, depthWrite: false, blending: THREE.AdditiveBlending, opacity: 0 });
    this.bolt = new THREE.LineSegments(new THREE.BufferGeometry(), this.boltMaterial); this.bolt.frustumCulled = false; this.bolt.visible = false; this.bolt.renderOrder = 5;
    scene.add(this.bolt);
    if (floor?.material instanceof THREE.MeshStandardMaterial) this.dry.copy(floor.material.color);
  }

  /** Applies the current conditions. Call after the time of day has set the lights and fog (`rig` is null on maps without that rig). */
  update(camera: THREE.OrthographicCamera, rig: WeatherRig | null, focus: { x: number; z: number }, seconds: number) {
    const { mist, rain, wet } = conditions, grey = Math.max(mist * .8, rain * .9);
    this.mist.visible = mist > .01; this.mistMaterial.uniforms.uMist.value = mist; this.mistMaterial.uniforms.uClear.value = 1 / camera.zoom; this.mistMaterial.uniforms.uTime.value = seconds; this.mistMaterial.uniforms.uFocus.value.set(focus.x, focus.z);
    this.rain.visible = rain > .02; this.rainMaterial.uniforms.uRain.value = rain; this.rainMaterial.uniforms.uTime.value = seconds;
    this.rainMaterial.uniforms.uFocus.value.set(focus.x, this.ground.height(focus.x, focus.z), focus.z); this.rainMaterial.uniforms.uSize.value = THREE.MathUtils.clamp(34 / camera.zoom, 12, 220);
    this.rainMaterial.uniforms.uWind.value.set(.12 * conditions.wind, .06 * conditions.wind);
    // Wet ground darkens and takes a dull sheen of the sky.
    if (this.floor?.material instanceof THREE.MeshStandardMaterial) { this.floor.material.color.copy(this.dry).multiplyScalar(1 - .32 * wet); this.floor.material.roughness = 1 - .4 * wet; }
    const flash = this.storm(seconds, focus);
    if (!rig) return;
    const { hemisphere, sun, fill, rim } = rig.lights;
    hemisphere.intensity *= 1 - .3 * rain - .05 * mist; sun.intensity *= 1 - .86 * rain - .3 * mist; fill.intensity *= 1 - .5 * rain; rim.intensity *= 1 - .9 * rain - .4 * mist;
    // Overcast light is grey: the sun and sky lose their colour as well as their strength.
    for (const light of [hemisphere, sun]) { this.grey.setScalar((light.color.r + light.color.g + light.color.b) / 3); light.color.lerp(this.grey, .6 * rain + .25 * mist); }
    this.renderer.toneMappingExposure *= 1 - .08 * rain;
    // The fog turns the colour of cloud and draws in: depth fades well before the edge of the view in thick mist.
    const average = (rig.fog.color.r + rig.fog.color.g + rig.fog.color.b) / 3;
    this.colour.setRGB(average * .96, average, average * 1.04 + .01);
    rig.fog.color.lerp(this.colour, .65 * grey);
    rig.fog.near *= 1 - .72 * mist - .4 * rain; rig.fog.far *= 1 - .55 * mist - .3 * rain;
    // A strike lights the whole scene for an instant, sky and ground alike.
    if (flash > 0) { hemisphere.intensity += 3.4 * flash; hemisphere.color.lerp(this.white, .7 * flash); sun.intensity += .8 * flash; rig.fog.color.lerp(this.flashColour, .5 * flash); }
    this.renderer.setClearColor(rig.fog.color);
    this.mistMaterial.uniforms.uColor.value.copy(rig.fog.color).lerp(this.white, .35);
  }

  /** The storm's flash now (0–1), the bolt for the strike that is landing, and any thunder that has just arrived. */
  private storm(seconds: number, focus: { x: number; z: number }): number {
    const { rain } = conditions, last = this.lastSeconds ?? seconds;
    this.lastSeconds = seconds;
    // Thunder is only voiced as time runs on; a jump (a new scenario, a long pause) would otherwise replay the sky.
    if (seconds > last && seconds - last < 1.5) for (const thunder of thunderBetween(last, seconds, rain, this.seed)) this.onThunder?.(thunder.strength, thunder.distance);
    const flash = flashAt(seconds, rain, this.seed), strike = strikesBetween(seconds - .5, seconds, rain, this.seed).find(item => item.start <= seconds && seconds - item.start < .5);
    if (strike && flash > .05) {
      if (strike.seed !== this.boltSeed) {
        this.boltSeed = strike.seed;
        this.bolt.geometry.setAttribute('position', new THREE.Float32BufferAttribute(boltSegments(strike.seed), 3));
        const reach = 14 + strike.distance * 46;
        const x = focus.x + Math.cos(strike.angle) * reach, z = focus.z + Math.sin(strike.angle) * reach;
        this.bolt.position.set(x, this.ground.height(x, z), z);
      }
      this.boltMaterial.opacity = Math.min(1, flash * 1.3); this.bolt.visible = true;
    } else this.bolt.visible = false;
    return flash;
  }

  dispose() {
    for (const object of [this.mist, this.rain, this.bolt]) { object.removeFromParent(); object.geometry.dispose(); }
    this.mistMaterial.dispose(); this.rainMaterial.dispose();
  }
}
